import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';

// Date d'ouverture des inscriptions : 23 juin 2025 à 19h
const REGISTRATION_OPEN_DATE = new Date('2025-06-23T19:00:00');

const Announcements = () => {
  const { language } = useLanguage();
  const [showAnnouncements, setShowAnnouncements] = useState(true);
  
  // Vérifier si on doit afficher les annonces (avant la date d'ouverture)
  useEffect(() => {
    const checkDate = () => {
      const now = new Date();
      setShowAnnouncements(now < REGISTRATION_OPEN_DATE);
    };
    
    checkDate();
    const interval = setInterval(checkDate, 60000); // Vérifier toutes les minutes
    
    return () => clearInterval(interval);
  }, []);
  
  // Si après la date d'ouverture, ne pas afficher cette section
  if (!showAnnouncements) {
    return null;
  }
  
  // Calculer le nombre de jours restants
  const daysRemaining = Math.ceil((REGISTRATION_OPEN_DATE - new Date()) / (1000 * 60 * 60 * 24));
  
  // Liste des annonces
  const announcements = [
    {
      icon: <AnnouncementIcon color="primary" />,
      text: getTranslation('home.announcement.registration', language) || 
            `Les inscriptions ouvrent dans ${daysRemaining} jour${daysRemaining > 1 ? 's' : ''} !`,
      key: 'registration'
    },
    {
      icon: <NewReleasesIcon color="secondary" />,
      text: getTranslation('home.announcement.proam', language) || 
            "Nouveau : division ProAm Jnj cette année",
      key: 'proam'
    },
    {
      icon: <NotificationsActiveIcon sx={{ color: 'success.main' }} />,
      text: getTranslation('home.announcement.earlybird', language) || 
            "Tarif Early bird disponible jusqu'au 15 juillet",
      key: 'earlybird'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
    >
      <Paper 
        elevation={3}
        sx={{ 
          p: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'primary.light',
          bgcolor: 'background.paper'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AnnouncementIcon color="primary" sx={{ mr: 1, fontSize: '1.8rem' }} />
          <Typography variant="h5" component="h2" color="primary.main">
            {getTranslation('home.announcements', language) || 'À la une'}
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <List disablePadding>
          {announcements.map((announcement, index) => (
            <ListItem 
              key={announcement.key}
              alignItems="flex-start"
              sx={{ 
                py: 1,
                px: 0,
                ...(index < announcements.length - 1 && {
                  borderBottom: '1px dashed',
                  borderColor: 'divider'
                })
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                {announcement.icon}
              </ListItemIcon>
              <ListItemText 
                primary={
                  <Typography variant="body1" fontWeight={500}>
                    {announcement.text}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </motion.div>
  );
};

export default Announcements;
